import React from 'react';
import { InputAdornment, SxProps, TextField } from '@mui/material';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import { useTranslation } from 'react-i18next';
import { FieldHookConfig, useField } from 'formik';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { setFieldOnSessionStorage } from '../../helpers/helpers';

type FormikNumberFieldProps = FieldHookConfig<string> & {
  label: string;
  id: string;
  formName?: string;
  objectId?: string;
  required?: boolean;
  fullWidth?: boolean;
  error?: string;
  type?: 'password' | 'tel' | 'text';
  suffix?: string;
  prefix?: string;
  allowNegative?: boolean;
  allowLeadingZeros?: boolean;
  decimalScale?: number;
  maxValue?: number;
  minValue?: number;
  isAllowed?: (values: NumberFormatValues) => boolean;
  clearError?: (errorKey: string) => void;
  helperText?: string;
  sx?: SxProps;
};

const FormikNumberField = (props: FormikNumberFieldProps) => {
  const [field, meta, helpers] = useField(props);
  const { setValue } = helpers;
  const {
    label,
    id,
    formName,
    objectId = '',
    required = false,
    fullWidth = true,
    type = 'text',
    suffix = '',
    prefix = '',
    allowLeadingZeros = false,
    allowNegative = false,
    decimalScale = undefined,
    maxValue = null,
    minValue = null,
    isAllowed,
    error,
    sx,
    clearError,
    helperText = '',
  } = props;

  const { t } = useTranslation();
  const changeField = (val: number | string) => {
    clearError?.(id);
    if (formName) {
      setFieldOnSessionStorage(formName, objectId, field.name, val);
    }

    setValue(val?.toString() || '');
  };

  return (
    <NumericFormat
      id={id}
      label={label}
      required={required}
      {...field}
      fullWidth={fullWidth}
      decimalScale={decimalScale}
      type={type}
      onValueChange={(values) => {
        if (isAllowed) {
          changeField(values.value);
        } else {
          changeField(values.floatValue);
        }
      }}
      value={field.value}
      allowNegative={allowNegative}
      valueIsNumericString
      allowLeadingZeros={allowLeadingZeros}
      customInput={TextField}
      isAllowed={(values) => {
        if (isAllowed) {
          return isAllowed(values);
        }
        const { floatValue } = values;
        if ((maxValue == null && minValue == null) || floatValue == null) return true;
        if (maxValue == null && minValue != null) return floatValue >= minValue;
        if (maxValue != null && minValue == null) return floatValue <= maxValue;
        return floatValue >= minValue && floatValue <= maxValue;
      }}
      helperText={
        (Boolean(error) && error) ||
        (meta.touched && meta.error && t(meta.error, { field: label })) ||
        helperText
      }
      error={Boolean(error) || (meta.touched && Boolean(meta.error))}
      sx={{
        marginBottom: { xs: '16px', sm: '24px' },
        ...sx,
      }}
      InputProps={{
        startAdornment: <InputAdornment position="start">{prefix}</InputAdornment>,
        endAdornment: (
          <InputAdornment position="end">
            {suffix}
            {meta.touched && Boolean(meta.error) && <WarningAmberIcon color="error" />}
          </InputAdornment>
        ),
      }}
    />
  );
};

export default FormikNumberField;
